import { translateMessage } from "@/i18n";
import { lazy, Suspense, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { Layout } from "./components/Layout";
import { NativeAppBridge } from "./components/NativeAppBridge";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AuthProvider } from "./hooks/useAuth";
import { I18nProvider, normalizeSupportedLocale, useI18n } from "./i18n";
import { useAuth } from "./hooks/useAuth";

const HomePage = lazy(() =>
  import("./pages/HomePage").then(({ HomePage }) => ({ default: HomePage }))
);
const LoginPage = lazy(() =>
  import("./pages/LoginPage").then(({ LoginPage }) => ({ default: LoginPage }))
);
const RegisterPage = lazy(() =>
  import("./pages/RegisterPage").then(({ RegisterPage }) => ({ default: RegisterPage }))
);
const ForgotPasswordPage = lazy(() =>
  import("./pages/ForgotPasswordPage").then(({ ForgotPasswordPage }) => ({
    default: ForgotPasswordPage,
  }))
);
const ResetPasswordPage = lazy(() =>
  import("./pages/ResetPasswordPage").then(({ ResetPasswordPage }) => ({
    default: ResetPasswordPage,
  }))
);
const VerifyEmailPage = lazy(() =>
  import("./pages/VerifyEmailPage").then(({ VerifyEmailPage }) => ({
    default: VerifyEmailPage,
  }))
);
const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then(({ DashboardPage }) => ({
    default: DashboardPage,
  }))
);
const BooksPage = lazy(() =>
  import("./pages/BooksPage").then(({ BooksPage }) => ({ default: BooksPage }))
);
const CollectionsPage = lazy(() =>
  import("./pages/CollectionsPage").then(({ CollectionsPage }) => ({
    default: CollectionsPage,
  }))
);
const BookDetailPage = lazy(() =>
  import("./pages/BookDetailPage").then(({ BookDetailPage }) => ({
    default: BookDetailPage,
  }))
);
const ReadingPage = lazy(() =>
  import("./pages/ReadingPage").then(({ ReadingPage }) => ({ default: ReadingPage }))
);
const StatsPage = lazy(() =>
  import("./pages/StatsPage").then(({ StatsPage }) => ({ default: StatsPage }))
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage").then(({ SettingsPage }) => ({ default: SettingsPage }))
);
const RecommendationsPage = lazy(() =>
  import("./pages/RecommendationsPage").then(({ RecommendationsPage }) => ({
    default: RecommendationsPage,
  }))
);
const NextBookPage = lazy(() =>
  import("./pages/NextBookPage").then(({ NextBookPage }) => ({
    default: NextBookPage,
  }))
);
const CommunityPage = lazy(() =>
  import("./pages/CommunityPage").then(({ CommunityPage }) => ({
    default: CommunityPage,
  }))
);
const HelpPage = lazy(() =>
  import("./pages/HelpPage").then(({ HelpPage }) => ({ default: HelpPage }))
);
function AuthenticatedLocaleSync() {
  const { user } = useAuth();
  const { setLocale } = useI18n();
  useEffect(() => {
    if (user?.ui_locale) {
      const accountLocale = normalizeSupportedLocale(user.ui_locale);
      setLocale(accountLocale);
    }
  }, [setLocale, user?.id, user?.ui_locale]);
  return null;
}
function App() {
  return (
    <AuthProvider>
      <I18nProvider>
        <AuthenticatedLocaleSync />
        <NativeAppBridge />
        <Suspense
          fallback={
            <div
              className="flex min-h-screen items-center justify-center bg-canvas"
              role="status"
            >
              <span className="h-10 w-10 animate-spin rounded-full border-2 border-line border-r-accent border-t-accent motion-reduce:animate-none" />
              <span className="sr-only">
                {translateMessage("common.Button.loading")}
              </span>
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<HomePage />} />
              <Route path="login" element={<LoginPage />} />
              <Route path="register" element={<RegisterPage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              <Route path="reset-password" element={<ResetPasswordPage />} />
              <Route path="verify-email" element={<VerifyEmailPage />} />

              <Route element={<ProtectedRoute />}>
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="books" element={<BooksPage />} />
                <Route path="books/collections" element={<CollectionsPage />} />
                <Route
                  path="books/collections/:collectionId"
                  element={<CollectionsPage />}
                />
                <Route path="books/:id" element={<BookDetailPage />} />
                <Route path="reading" element={<ReadingPage />} />
                <Route path="stats" element={<StatsPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="discover" element={<RecommendationsPage />} />
                <Route path="next-book" element={<NextBookPage />} />
                <Route path="community" element={<CommunityPage />} />
                <Route path="help" element={<HelpPage />} />
              </Route>

              <Route
                path="*"
                element={<div>{translateMessage("common.App.notFound")}</div>}
              />
            </Route>
          </Routes>
        </Suspense>
      </I18nProvider>
    </AuthProvider>
  );
}
export default App;
